import React, { useState, useEffect } from "react";
import ProductGallery from "components/product-gallery";
import OkendoStarRating from "components/okendo/star-rating";
import Money from "components/money";
import { ReactComponent as Badge } from "assets/svgs/rounded-badge.svg";
import AddToCart from "components/add-to-cart";
import Accordions from "components/accordions";
import { DropdownOption } from "components/product/options";
import { useSettings } from "hooks/useStore";
import Image from "components/image";
import ButtonLink from "components/button-link";
import { SCREEN_SIZES } from "utils/constants";
import { useMediaQuery } from "react-responsive";
import { scrollToElementById } from "helpers/scroll";
import { reviewsWidgetId } from "utils/constants";
import clsx from "clsx/lite";
import ShoppingCart from "components/shopping-cart";

const DiscountPrice = ({ selectedVariant }) => {
	const { customerCountry } = useSettings(["customerCountry", "currencyCode"]);
	const isUS = customerCountry === "US";
	const marketData = selectedVariant.markets?.[customerCountry.toLowerCase()] || {};

	const compareAtPrice = isUS ? selectedVariant?.compareAtPrice : +marketData.compareAtPrice;
	const price = isUS ? selectedVariant?.price : +marketData.price;

	const discountPercentage = ((compareAtPrice - price) / compareAtPrice) * 100;

	if (discountPercentage <= 0) return null;

	return (
		<span className="text-lg md:text-xl font-bold text-[#6E3CFF] bg-[#7e55f424] py-1.5 px-2.5">
			{discountPercentage.toFixed(0)}% off
		</span>
	);
};

const BadgeList = ({ badgeList, className, iconClass, textClass }) => (
	badgeList.map((badgeText, index) => (
		<div
			key={index}
			className={clsx(
				"flex items-center gap-1 md:gap-3 p-1 bg-[#F7F7F7] rounded-3xl pr-4",
				className
			)}
		>
			<Badge className={iconClass} />
			<span className={textClass}>{badgeText}</span>
		</div>
	))
);

const DetailsWithVariantSelector = ({ product, selectedVariant, data, productTabs, setSelectedVariant, }) => {
	const { badgeList = [], stickyButton = {}, belowAtcImage } = data;
	const { id, title, reviewAggregate, options, variants } = product;
	const [isSticky, setIsSticky] = useState(false);
	const isMobile = useMediaQuery({ maxWidth: SCREEN_SIZES.sm });

	const handleScroll = () => {
		const productSection = document.getElementById("product-section");
		if (productSection) {
			const { bottom } = productSection.getBoundingClientRect();
			setIsSticky(bottom <= 0);
		}
	};

	useEffect(() => {
		if (!isMobile && stickyButton) return;
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [isMobile]);

	return (
		<>
			<div className="flex flex-col gap-6">
				<div className="flex flex-col gap-3">
					<button onClick={() => scrollToElementById(reviewsWidgetId)}>
						<OkendoStarRating
							reviewAggregate={reviewAggregate}
							customText="Reviews"
							removeParenthesis
							starsWidth="w-24"
						/>
					</button>
					<h2 className="font-base font-bold md:text-h3">{title}</h2>
					<div className="flex flex-wrap justify-between md:justify-normal md:gap-3">
						<div className="flex items-center gap-3">
							{selectedVariant?.compareAtPrice > selectedVariant?.price && (
								<span className="line-through text-lg text-secondary pt-1">
									<Money
										value={selectedVariant.compareAtPrice}
										markets={selectedVariant.markets}
										compareAtPrice />
								</span>
							)}
							<span className="font-bold md:font-semibold text-2xl mb-0 pb-0 w-fit text-right">
								<Money value={selectedVariant?.price} markets={selectedVariant?.markets} />
							</span>
						</div>
						<DiscountPrice selectedVariant={selectedVariant} />
					</div>
				</div>
				<hr />
				{badgeList.length > 0 &&
					<div className="hidden lg:w-5/6 md:flex flex-wrap gap-3 md:gap-2">
						<BadgeList badgeList={badgeList} className="w-fit pl-1 !gap-2"
							iconClass="[&>rect]:fill-yellow w-3.5 md:w-4"
							textClass="font-normal text-sm md:text-base" />
					</div>
				}
				<div className="lg:w-5/6">
					<AddToCart
						quantityConfig={{ defaultValue: 2 }}
						product={{ title, id, variant: selectedVariant, variants }}
						buttonClass="btn-quinary text-black h-14"
						hideWishlistIcon>
						<div className="flex-1 md:w-1/2">
							{options.map((option) => (
								option.values.length < 2 ? null :
									<DropdownOption
										option={option}
										variant={selectedVariant}
										key={option.name}
										setSelectedVariant={setSelectedVariant}
										options={options}
										variants={variants}
										renderAsLink={false} />
							))}
						</div>
					</AddToCart>
					{belowAtcImage && <Image image={belowAtcImage} className="mt-3 w-full" />}
				</div>
				{badgeList.length > 0 &&
					<div className="md:hidden flex flex-col gap-3 md:gap-2">
						<BadgeList badgeList={badgeList} className="w-fit pl-1 !gap-2"
							iconClass="[&>rect]:fill-yellow w-3.5 md:w-4"
							textClass="font-normal text-sm md:text-base" />
					</div>
				}
				{productTabs.length > 0 && (
					<div className="w-full lg:w-5/6">
						<Accordions
							data={{ items: productTabs }}
							itemsTitleClass="font-semibold text-start text-base font-base"
							className="bg-cream !p-4 !pb-0 my-4 !border-b-0" />
					</div>
				)}
			</div>
			<ButtonLink data={stickyButton} className={clsx("fixed bottom-0 w-11/12 mx-auto btn-quinary z-10 left-0",
				" transition-transform duration-300 text-center sm:!hidden h-14 content-center",
				isSticky ? "translate-y-0" : "translate-y-full hidden")}
			/>
		</>
	);
};

const DetailsWithoutVariantSelector = ({ badgeList, product, data }) => {
	const { title, reviewAggregate } = product;
	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-3">
				<button onClick={() => scrollToElementById(reviewsWidgetId)}>
					<OkendoStarRating reviewAggregate={reviewAggregate} customText={`+ Happy ${title} Customers`}
						removeParenthesis starsWidth="w-24" />
				</button>
				<h2 className="font-base md:leading-tight">{`Elevate Your Exterior with ${title}`}</h2>
				{data.description && <p className="text-sm md:leading-5 md:text-base py-2">{data.description}</p>}
				{data.lineBelowDescription && <hr />}
			</div>
			{badgeList.length > 0 &&
				<div className="flex flex-col gap-4 w-fit">
					<BadgeList badgeList={badgeList} className="w-fit"
						iconClass="[&>rect]:fill-yellow w-3.5 md:w-5"
						textClass="font-normal text-sm md:text-xl" />
				</div>}
			<ButtonLink data={data.cta} className="md:mt-10 mb-4 w-full md:w-fit font-bold" />
		</div>
	);
};

const ProductDetails = ({ data }) => {
	const { product, badgeList = [], customImages = [] } = data;
	const { id, title, variant, images, tabs } = product;
	const hideVariantSelector = data?.hideVariantSelector;

	const [selectedVariant, setSelectedVariant] = useState(variant);
	const [isSticky, setIsSticky] = useState(false);
	const productTabs = [].concat(tabs).concat(selectedVariant?.tabs).filter((tab) => tab?.title !== "Common Questions");
	const useCustomImages = hideVariantSelector && customImages.length > 0;

	useEffect(() => {
		const handleScroll = () => {
			const allSections = document.querySelectorAll(".product-section");
			let isAnySectionInView = false;

			allSections.forEach(section => {
				const rect = section.getBoundingClientRect();
				const isInView = rect.top < window.innerHeight && rect.bottom >= 0;

				if (isInView) {
					isAnySectionInView = true;
				}
			});

			setIsSticky(!isAnySectionInView);
		};

		window.addEventListener("scroll", handleScroll);

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);



	return (
		<>
			{data.stickyBar?.text &&
				<>
					<div className={clsx("hidden md:block fixed bg-cream w-full z-50 left-0 py-1.5",
						"transition-transform duration-150 ease-out top-0",
						isSticky ? "translate-y-0 shadow-xl" : "-translate-y-full")}>
						<div className="container-fluid-padding w-full flex justify-between items-center">
							<div className="flex flex-col">
								<button onClick={() => scrollToElementById(reviewsWidgetId)}>
									<OkendoStarRating />
								</button>
								<span className="text-3xl font-bold">{title}</span>
								<span className="text-lg">{data.stickyBar.text}</span>
							</div>
							<div className="flex items-center gap-4">
								<ButtonLink data={data.stickyBar.button} />
								<ShoppingCart />
							</div>
						</div>
					</div>
					<div className={clsx("block md:hidden fixed bg-cream w-full z-50 left-0 pt-4 pb-2",
						"transition-transform duration-300 top-0",
						isSticky ? "translate-y-0 shadow-xl" : "-translate-y-full")}>
						<div className="container-fluid-padding w-full flex justify-center items-center">
							<div className="flex flex-col flex-1 pr-4 pl-8 items-center gap-1">
								<ButtonLink data={data.stickyBar.button} className="w-full" />
								<button onClick={() => scrollToElementById(reviewsWidgetId)}>
									<OkendoStarRating starsColor="#111111" starsWidth="w-20"
										customText="Loved by over 3,524 customers" />
								</button>
							</div>
							<div className="flex items-center justify-center pb-6">
								<ShoppingCart />
							</div>
						</div>
					</div>
				</>}
			<div className={clsx("product-section flex flex-col md:flex-row standard-section-width gap-4 mx-auto",
				hideVariantSelector ? "md:gap-16" : "md:gap-7")} id={!hideVariantSelector ? "product-section" : ""}>
				<div className="md:max-w-screen-sm justify-self-end md:w-1/2 relative">
					{data.badgeIcon &&
						<div className="absolute top-0 left-4 z-10 w-[87.5px] md:w-[135px]">
							<Image image={data.badgeIcon} className="relative" />
						</div>}
					{data.label &&
						<span className="absolute top-4 right-4 z-10 text-sm md:text-lg 
							text-[#1B0069] bg-[#EDE7FE] px-4 py-2 font-bold w-fit">
							{data.label}
						</span>
					}
					<ProductGallery
						key={selectedVariant.id}
						className="sticky top-16"
						productId={id}
						title={title}
						productImages={useCustomImages ? customImages : images || []}
						variantImages={useCustomImages ? [] : selectedVariant?.images || []}
						showFiveSlides
						hideCylindoDynaimcFeature
					/>
				</div>
				<div className="md:w-1/2">
					{hideVariantSelector
						? <DetailsWithoutVariantSelector
							badgeList={badgeList}
							product={product}
							data={data}
						/>
						: <DetailsWithVariantSelector
							product={product}
							selectedVariant={selectedVariant}
							data={data}
							productTabs={productTabs}
							setSelectedVariant={setSelectedVariant} />
					}
				</div>
			</div>
		</>
	);
};

export default ProductDetails;